/**
 * BotID-Safe API Fetch Wrapper
 * 
 * Ensures BotID client-side signals have time to be collected before
 * making API calls. This prevents the initial page load API calls from
 * being blocked because signals weren't ready yet.
 * 
 * Usage:
 * - Use `fetchWithDelay` for initial page load fetches
 * - Use regular `fetch` for user-triggered actions (clicks, form submits)
 */

// Track if BotID has had time to initialize
let botIdReady = false
let botIdReadyPromise: Promise<void> | null = null

// Delay in milliseconds to wait for BotID to collect signals
// Reduced to 0 - BotID signals are sent via headers automatically
// The middleware handles verification, not the client blocking
const BOTID_INIT_DELAY_MS = 0

/**
 * Initialize BotID readiness tracking
 * Call this once when the app mounts
 */
export function initBotIdReadiness(): void {
  if (typeof window === 'undefined') return
  
  if (!botIdReadyPromise) {
    botIdReadyPromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        botIdReady = true
        resolve()
      }, BOTID_INIT_DELAY_MS)
    })
  }
}

/**
 * Wait for BotID to be ready before proceeding
 */
export async function waitForBotId(): Promise<void> {
  if (botIdReady) return
  
  if (!botIdReadyPromise) {
    // If not initialized, initialize now
    initBotIdReadiness()
  }
  
  await botIdReadyPromise
}

/**
 * Fetch with BotID delay - use for initial page load API calls
 * 
 * This ensures the BotID signals have been collected before the
 * API call is made, preventing legitimate users from being blocked.
 * 
 * @param input - URL or Request object
 * @param init - Fetch options
 * @returns Promise<Response>
 * 
 * @example
 * ```ts
 * // In a useEffect for initial data fetch
 * const response = await fetchWithDelay('/api/collections/refrigerators')
 * ```
 */
export async function fetchWithDelay(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  // Wait for BotID to be ready (only delays first ~200ms on page load)
  await waitForBotId()
  
  // Now make the actual fetch
  return fetch(input, init)
}

/**
 * Hook-style function to get a fetch wrapper that's safe for initial loads
 * Tracks per-component whether initial delay has been applied
 */
export function createSafeFetch() {
  let hasDelayed = false
  
  return async function safeFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    // Only delay the first fetch per component
    if (!hasDelayed && !botIdReady) {
      await waitForBotId()
      hasDelayed = true
    }
    
    return fetch(input, init)
  }
}

/**
 * React hook for BotID-safe fetching
 * Returns a fetch function that delays the first call to wait for BotID
 */
export function useSafeFetch() {
  // Each component instance gets its own tracking
  let delayed = false
  
  return async function (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    if (!delayed && !botIdReady) {
      await waitForBotId()
      delayed = true
    }
    return fetch(input, init)
  }
}

